import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Mail, Lock, Globe, Clock, Save, CheckCircle, ChevronLeft, Shield, Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, X, MailCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SelectDropdown } from '../components/SelectDropdown';
const LANGUAGE_OPTIONS = [{
  value: 'en',
  label: 'English (US)'
}, {
  value: 'es',
  label: 'Español'
}, {
  value: 'fr',
  label: 'Français'
}, {
  value: 'de',
  label: 'Deutsch'
}, {
  value: 'zh',
  label: '中文 (Chinese)'
}, {
  value: 'ja',
  label: '日本語 (Japanese)'
}];
const TIMEZONE_OPTIONS = [{
  value: 'UTC-8',
  label: 'Pacific Time (US & Canada) (UTC-08:00)'
}, {
  value: 'UTC-7',
  label: 'Mountain Time (US & Canada) (UTC-07:00)'
}, {
  value: 'UTC-6',
  label: 'Central Time (US & Canada) (UTC-06:00)'
}, {
  value: 'UTC-5',
  label: 'Eastern Time (US & Canada) (UTC-05:00)'
}, {
  value: 'UTC+0',
  label: 'Greenwich Mean Time (UTC+00:00)'
}, {
  value: 'UTC+1',
  label: 'Central European Time (UTC+01:00)'
}, {
  value: 'UTC+8',
  label: 'China Standard Time (UTC+08:00)'
}, {
  value: 'UTC+9',
  label: 'Japan Standard Time (UTC+09:00)'
}];
export function ProfilePage() {
  // Personal Info State
  const [fullName, setFullName] = useState('Linda Taylor');
  const [email] = useState('linda.taylor@example.com'); // Read-only
  const [nameError, setNameError] = useState<string | null>(null);
  // Password Reset State
  const [resetStatus, setResetStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [resetError, setResetError] = useState<string | null>(null);
  // Preferences State
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC-5');
  const [languageError, setLanguageError] = useState<string | null>(null);
  const [timezoneError, setTimezoneError] = useState<string | null>(null);
  // UI State

  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  // Open the Settings tab when arriving from a settings link
  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (requestedTab && ['general', 'security', 'preferences'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const validateFullName = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return 'Full name is required.';
    if (trimmed.length < 2) return 'Full name must be at least 2 characters.';
    if (trimmed.length > 60) return 'Full name must be 60 characters or fewer.';
    if (!/^[\p{L}][\p{L}\s'.-]*$/u.test(trimmed)) {
      return 'Use letters, spaces, hyphens, periods, or apostrophes only.';
    }
    if (trimmed.split(/\s+/).length < 2) {
      return 'Enter both a first and last name.';
    }
    return null;
  };
  const runSave = () => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 1000);
  };
  const handleSaveGeneral = () => {
    const error = validateFullName(fullName);
    setNameError(error);
    if (error) {
      setSaveStatus('idle');
      return;
    }
    runSave();
  };
  const handleSavePreferences = () => {
    const langError = language ? null : 'Select a language.';
    const tzError = timezone ? null : 'Select a time zone.';
    setLanguageError(langError);
    setTimezoneError(tzError);
    if (langError || tzError) {
      setSaveStatus('idle');
      return;
    }
    runSave();
  };
  const handleSendResetEmail = () => {
    if (!email) {
      setResetError('We don’t have an email address on file for your account. Contact technical support to reset your password.');
      return;
    }
    setResetError(null);
    setResetStatus('sending');
    setTimeout(() => setResetStatus('sent'), 800);
  };
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2573" data-mp="3sjdcja0x2m9q">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2574" data-mp="3ojw4c8u3vb0v">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2575" data-mp="1y5vq5qc8zvga">
          <div className="flex items-center h-16" data-id="element-2576" data-mp="1y7yn6ubtioui">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2577" data-mp="1u64rz1ke6d1q">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2578" data-mp="1k1erl04mzgzm">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2579" data-mp="1o0x69e5v6vu0">
                <Home size={18} data-id="element-2580" data-mp="1f9d8au91wq03" />
                <span className="hidden sm:inline" data-id="element-2581" data-mp="1f8byet3ru9jy">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2582" data-mp="10bl06n5u33vx">
                <BarChart3 size={18} data-id="element-2583" data-mp="rchbn3nwdwa1" />
                <span className="hidden sm:inline" data-id="element-2584" data-mp="rdiqlj81b2x1">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2585" data-mp="wbty3naux1c3">
                <FileText size={18} data-id="element-2586" data-mp="2qplfxa3carim" />
                <span className="hidden sm:inline" data-id="element-2587" data-mp="2qojv4dwiuqnp">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2588" data-mp="2ob6jnicpbyaq">
                <UserCog size={18} data-id="element-2589" data-mp="2f8x94shfo4xc" />
                <span className="hidden sm:inline" data-id="element-2590" data-mp="2fa8lvhsuwn0l">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2591" data-mp="37y4k4qy2qmzh">
              <Link data-phoebe-nav="/alerts" to="/alerts" aria-label="Notifications" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2592" data-mp="380gvumd8o143">
                <Bell size={20} data-id="element-2593" data-mp="2y79v5etg19ac" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2594" data-mp="2y6hxzzk773hy"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2595" data-mp="3qovotf749p7j">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2596" data-mp="3qqght0ds903a">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2597" data-mp="3ta3hgu5o60oa">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2598" data-mp="71kc834fd66f">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2599" data-mp="c2rgvtb9tp8v" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2600" data-mp="g0pad8c2qxi2">
                    <div className="py-2" data-id="element-2601" data-mp="psv1kfui5aw7">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2602" data-mp="ptwekpv2w7gk">
                        <Users size={18} className="text-gray-600" data-id="element-2603" data-mp="zr0zynh3nywb" />
                        <span className="font-medium text-gray-900" data-id="element-2604" data-mp="zoo9tn16ui86">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2605" data-mp="1p29qc0r4bnt0">
                        <Home size={18} className="text-gray-600" data-id="element-2606" data-mp="1l3sfp78hnv6u" />
                        <span className="font-medium text-gray-900" data-id="element-2607" data-mp="1z060ap8bkcpj">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2608" data-mp="16ca5xr682wvx">
                        <UserCog size={18} className="text-gray-600" data-id="element-2609" data-mp="11fjnix6y3p7c" />
                        <span className="font-medium text-gray-900" data-id="element-2610" data-mp="1g3o7s5lnc8s8">
                          Admin Hub
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2611" data-mp="nm255fpzwiiw"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2612" data-mp="nk90g5yy08dj">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2613" data-mp="2ua2lhetv3l6f">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2614" data-mp="2lafqbarq7blt"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2615" data-mp="2pbhjb9v1urfc"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2616" data-mp="2p9etaptwcyam"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2617" data-mp="3cyzcn9typxkf">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2618" data-mp="391bg62cmfwqf">
        <div data-phoebe-tour="profile" className="mb-8" data-id="element-2619" data-mp="1sjnpfzzptys5">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2620" data-mp="1xkuwtxxy4vya">Profile</h1>
          <p className="text-gray-600" data-id="element-2621" data-mp="3e2hncc7945ra">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-2622" data-mp="3i4co9fl9sh4j">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-3" data-id="element-2623" data-mp="34dhn6ph0j4bi">
            <nav className="space-y-1 sticky top-24" data-id="element-2624" data-mp="34alqf4gwilon">
              <button onClick={() => setActiveTab('general')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'general' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2625" data-mp="39azn5qd1y97d">
                <User size={18} data-id="element-2626" data-mp="2ldtzq9z154ak" />
                General
              </button>
              <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'security' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2627" data-mp="2lfx09nxt6ftj">
                <Shield size={18} data-id="element-2628" data-mp="ok8zlfit2d04" />
                Security
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'preferences' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2629" data-mp="yop9ls9lszw0">
                <Globe size={18} data-id="element-2630" data-mp="12rc9mggfhr4a" />
                Preferences
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6" data-id="element-2631" data-mp="1hef4sgha3nog">
            {/* General Section */}
            {activeTab === 'general' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2632" data-mp="1cizutzme6bxb">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2633" data-mp="3s7rp5tem4rlc">
                  <div className="p-6 border-b border-gray-200" data-id="element-2634" data-mp="3o6y3z7sk1qw5">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2635" data-mp="3o8iveojkotps">
                      Personal Information
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2636" data-mp="1w4dagxxuei9b">
                      Update your personal details.
                    </p>
                  </div>

                  <div className="p-6 space-y-8" data-id="element-2637" data-mp="216cema1epj7b">
                    {/* Avatar */}
                    <div className="flex items-center gap-6" data-id="element-2638" data-mp="264dy2w2qvkjb">
                      <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-4 border-white shadow-md" data-id="element-2639" data-mp="2a3mq9wi4zacr">
                        <span className="text-2xl font-bold text-gray-500" data-id="element-2640" data-mp="2f1o8kf7qn5n6">
                          LT
                        </span>
                      </div>
                      <div data-id="element-2641" data-mp="2osivllst699o">
                        <h3 className="font-medium text-gray-900" data-id="element-2642" data-mp="2osskt7614ceb">
                          Linda Taylor
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-2643" data-mp="2xtz6dunjcdef">
                          This will be displayed on your profile.
                        </p>
                      </div>
                    </div>

                    <div data-id="element-2644" data-mp="i4sbvt4hsc0t">
                      <label htmlFor="full-name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2645" data-mp="i4iirwo5hoqw">
                        Full Name
                      </label>
                      <input id="full-name" type="text" value={fullName} onChange={e => {
                    setFullName(e.target.value);
                    if (nameError) setNameError(validateFullName(e.target.value));
                  }} onBlur={e => setNameError(validateFullName(e.target.value))} aria-invalid={Boolean(nameError)} aria-describedby={nameError ? 'full-name-error' : undefined} className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-shadow ${nameError ? 'border-[#DB3725] focus:border-[#DB3725] focus:ring-[#DB3725]/30' : 'border-gray-300 focus:border-[#30718D] focus:ring-[#30718D]/30'}`} data-id="element-2646" data-mp="87nlfh3m5ue7" />
                      {nameError && <p id="full-name-error" role="alert" className="text-[#DB3725] text-xs mt-1 flex items-center gap-1" data-id="element-2647" data-mp="1jskni7qnfofg">
                          <X size={12} className="flex-shrink-0" data-id="element-2648" data-mp="18dzja1qdb79g" /> {nameError}
                        </p>}
                    </div>

                    <div data-id="element-2649" data-mp="ufhxi9k0w4u6">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2650" data-mp="ugauwjx3dhzn">
                        Email Address
                      </label>
                      <div className="relative" data-id="element-2651" data-mp="1n2vn1mle5y49">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2652" data-mp="1n58f4skv5ov1" />
                        <input type="email" value={email} readOnly className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed" data-id="element-2653" data-mp="1s41hppym3vgf" />
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-4" data-id="element-2654" data-mp="2617xdbo1he1f">
                    {nameError && <p className="mr-auto text-sm text-[#DB3725] font-medium" data-id="element-2655" data-mp="2aut0fi4hoy7h">
                        Fix the errors above to save your changes.
                      </p>}
                    <button onClick={handleSaveGeneral} disabled={saveStatus === 'saving'} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm disabled:opacity-70" data-id="element-2656" data-mp="3qj8xk0mwpx6x">
                      {saveStatus === 'saving' ? <>Saving...</> : saveStatus === 'success' ? <>
                          <CheckCircle size={18} data-id="element-2657" data-mp="37lfw72rxlvbn" />
                          Saved
                        </> : <>
                          <Save size={18} data-id="element-2658" data-mp="3llh06r3htivc" />
                          Save Changes
                        </>}
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Security Section */}
            {activeTab === 'security' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2659" data-mp="vvnelfroogpm">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2660" data-mp="3ulwxs47ohpqp">
                  <div className="p-6 border-b border-gray-200" data-id="element-2661" data-mp="2jyr1rq56x7s">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2662" data-mp="7i0cukeydamy">
                      Password & Security
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2663" data-mp="36y59e28v5m92">
                      Manage your password and account security.
                    </p>
                  </div>

                  <div className="p-6" data-id="element-2664" data-mp="3fxa93bw5vlmt">
                    <div className="flex items-start gap-4" data-id="element-2665" data-mp="3fw8y17f5az53">
                      <div className="w-10 h-10 rounded-full bg-[#30718D]/10 flex items-center justify-center flex-shrink-0" data-id="element-2666" data-mp="3kp1xpgvrbhab">
                        <Lock size={20} className="text-[#30718D]" data-id="element-2667" data-mp="2n8d2w7585faf" />
                      </div>
                      <div className="min-w-0" data-id="element-2668" data-mp="2s6wp88nqct3b">
                        <h3 className="font-medium text-gray-900" data-id="element-2669" data-mp="2kooqiisbvevg">
                          Change your password
                        </h3>
                        <p className="text-sm text-gray-500 mt-1 max-w-xl" data-id="element-2670" data-mp="2fqnak6q77vo7">
                          For your security, passwords are changed through a
                          verified email link. We'll send a reset link to{' '}
                          <span className="font-medium text-gray-700" data-id="element-2671" data-mp="26r06te9zydrz">
                            {email}
                          </span>
                          .
                        </p>
                      </div>
                    </div>

                    {resetError && <div role="alert" className="bg-[#DB3725]/5 border border-[#DB3725]/20 rounded-lg p-4 mt-4" data-id="element-2672" data-mp="1s12nbbn0ic94">
                        <p className="text-sm text-[#DB3725] font-medium" data-id="element-2673" data-mp="1n2j0za4iayg8">
                          {resetError}
                        </p>
                      </div>}
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2674" data-mp="1e1ktghuv08sw">
                    <button onClick={handleSendResetEmail} disabled={resetStatus === 'sending'} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm disabled:opacity-70" data-id="element-2675" data-mp="31y97f0n0iopt">
                      <Mail size={18} data-id="element-2676" data-mp="2wxl8lssjl3l1" />
                      {resetStatus === 'sending' ? 'Sending...' : 'Send password reset email'}
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Preferences Section */}
            {activeTab === 'preferences' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2677" data-mp="298sjogac7vzx">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-id="element-2678" data-mp="208x1vsma12qx">
                  <div className="p-6 border-b border-gray-200" data-id="element-2679" data-mp="255niqi5wo2de">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2680" data-mp="1lkn9e5lczeos">
                      Preferences
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2681" data-mp="1gmu5xkbfwys7">
                      Customize your platform experience.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-2682" data-mp="1qk894i8crr2k">
                    <div data-id="element-2683" data-mp="1qif231ttv4u1">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2684" data-mp="1qmcn5szyu6gk">
                        Language
                      </label>
                      <SelectDropdown fullWidth ariaLabel="Language" value={language} onChange={value => {
                    setLanguage(value);
                    setLanguageError(value ? null : 'Select a language.');
                  }} options={LANGUAGE_OPTIONS} icon={<Globe size={18} data-id="element-2686" data-mp="29cu3ey3m39l1" />} invalid={Boolean(languageError)} describedBy={languageError ? 'language-error' : undefined} data-id="element-2685" data-mp="1zjmzlhois1n2" />
                      {languageError && <p id="language-error" role="alert" className="text-[#DB3725] text-xs mt-1 flex items-center gap-1" data-id="element-2687" data-mp="2ich39fgp07bz">
                          <X size={12} className="flex-shrink-0" data-id="element-2688" data-mp="2us2xahkfe80r" /> {languageError}
                        </p>}
                    </div>

                    <div data-id="element-2689" data-mp="34rtoa99s24w3">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2690" data-mp="34r0q3wovwnpk">
                        Time Zone
                      </label>
                      <SelectDropdown fullWidth scrollMenu ariaLabel="Time zone" value={timezone} onChange={value => {
                    setTimezone(value);
                    setTimezoneError(value ? null : 'Select a time zone.');
                  }} options={TIMEZONE_OPTIONS} icon={<Clock size={18} data-id="element-2692" data-mp="2lusjaog844s5" />} invalid={Boolean(timezoneError)} describedBy={timezoneError ? 'timezone-error' : undefined} data-id="element-2691" data-mp="2zr61jzsl0a7o" />
                      {timezoneError && <p id="timezone-error" role="alert" className="text-[#DB3725] text-xs mt-1 flex items-center gap-1" data-id="element-2693" data-mp="1tcn46xdoyic6">
                          <X size={12} className="flex-shrink-0" data-id="element-2694" data-mp="1ocsid88rwixz" /> {timezoneError}
                        </p>}
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-4" data-id="element-2695" data-mp="8mhtoskkwtlh">
                    {(languageError || timezoneError) && <p className="mr-auto text-sm text-[#DB3725] font-medium" data-id="element-2696" data-mp="3r2253a7dum35">
                        Fix the errors above to save your preferences.
                      </p>}
                    <button onClick={handleSavePreferences} disabled={saveStatus === 'saving'} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm disabled:opacity-70" data-id="element-2697" data-mp="mm0wd5umfcqe">
                      {saveStatus === 'saving' ? <>Saving...</> : saveStatus === 'success' ? <>
                          <CheckCircle size={18} data-id="element-2698" data-mp="imigiohnpruf" />
                          Saved
                        </> : <>
                          <Save size={18} data-id="element-2699" data-mp="33amzn8kb7ono" />
                          Save Preferences
                        </>}
                    </button>
                  </div>
                </div>
              </motion.div>}

          </div>
        </div>
      </div>

      {/* Password Reset Sent Modal */}
      <AnimatePresence data-id="element-2700" data-mp="z4v71g5rrvvc">
        {resetStatus === 'sent' && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setResetStatus('idle')} data-id="element-2701" data-mp="z3k2he4i2k36">
            <motion.div role="dialog" aria-modal="true" aria-labelledby="reset-sent-title" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-lg p-8 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2702" data-mp="q46uyqzxs5l3">
              <div className="flex justify-end -mt-2 -mr-2" data-id="element-2703" data-mp="2ahxm26s80dde">
                <button onClick={() => setResetStatus('idle')} className="text-sm text-gray-900 hover:text-gray-700 underline transition-colors" data-id="element-2704" data-mp="26jrn3kxmj231">
                  Close
                </button>
              </div>

              <div className="flex flex-col items-center text-center" data-id="element-2705" data-mp="3tdlbkoix3j5l">
                <div className="w-16 h-16 rounded-full bg-[#007558] flex items-center justify-center" data-id="element-2706" data-mp="21jpf4rnvusp">
                  <MailCheck size={32} className="text-white" data-id="element-2707" data-mp="610wg2mqkzzz" />
                </div>

                <h2 id="reset-sent-title" className="mt-4 text-xl font-bold text-[#1A3D4D]" data-id="element-2708" data-mp="35hz6r5e0x1dq">
                  Check your email
                </h2>

                <p className="mt-4 text-sm text-black leading-5" data-id="element-2709" data-mp="3aiejbuqhe4un">
                  We've sent a message to {email}. Click the link in the message
                  to reset your password.
                </p>
                <p className="mt-2 text-sm text-[#1A3D4D] leading-5" data-id="element-2710" data-mp="3jem4vbhppxl8">
                  Didn't receive the message?{' '}
                  <a href="#" className="font-medium underline hover:text-[#30718D] transition-colors" data-id="element-2711" data-mp="yqhlqrf280rz">
                    Contact technical support
                  </a>
                  .
                </p>

                <button onClick={() => setResetStatus('idle')} className="mt-7 px-8 py-2 min-w-[12rem] bg-[#DB3725] text-white rounded-full hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-2712" data-mp="sbaosutm8azf">
                  Done
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}